import { gql, useQuery } from "@apollo/client";
import type { ICollectionHandleQuery } from "@repo/shopify/queries/collection-gql";
import { collectionHandleQuery } from "@repo/shopify/queries/collection-gql";
import type { ProductEdge, ProductVariantConnection } from "@shopify/hydrogen-react/storefront-api-types";
import qs from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { COLLECTION_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import { useLayoutStore } from "@store/layout";

const useCollections = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [searchParams] = useSearchParams();

  const { openToast, setMeta } = useLayoutStore();

  const [collectionData, setCollectionData] = useState<ICollectionHandleQuery>();
  const [products, setProducts] = useState<ProductEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ProductEdge[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handle = pathname.includes("collections") && pathname.split("/").pop();

  const queryString = qs.parse(search);

  const prevFilter = useRef<typeof variables.filters>([]);
  const handleRef = useRef<string>("");
  const productLength = useRef(0); // 이전 products의 길이 저장

  const hasNextPage = useMemo(
    () => collectionData?.collection.products.pageInfo.hasNextPage || false,
    [collectionData?.collection.products.pageInfo],
  );

  const endCursor = useMemo(
    () => collectionData?.collection.products.pageInfo.endCursor,
    [collectionData?.collection.products.pageInfo],
  );

  const variables = useMemo(() => {
    // variables이 바뀌면 재조회 하기 때문에 값 초기화
    setExpandProducts([]);
    productLength.current = 0;

    // category / color / size
    const sortQueries = COLLECTION_SORT.find((item) => item.name === queryString.sort);
    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) =>
          key === "category" ? { productType: item } : { variantOption: { name: key, value: item } },
        );
      })
      .flat();

    return {
      first: searchParams.get("view") && searchParams.get("view") === "seperate" ? 12 : 25, // 쿼리 1회에 조회하는 product 수
      handle,
      sortKey: sortQueries?.value ?? "COLLECTION_DEFAULT",
      reverse: sortQueries?.reverse ?? false,
      filters: filterQueries || [],
    };
  }, [handle, search]);

  const getExpandList = (productList: ProductEdge[]) => {
    const expandList: ProductEdge[] = [];

    productList.forEach(({ node }) => {
      const colorOptions = node.options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = node.variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ProductEdge = {
            node: {
              ...node,
              options: node.options.map((option) =>
                option.name === "Color" ? { ...option, values: [color] } : option,
              ),
              variants: { edges: colorList } as ProductVariantConnection,
              availableForSale: colorList.every(({ node }) => node.availableForSale),
            },
          } as ProductEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  /** 컬렉션 데이터 쿼리 */
  const { loading, fetchMore, refetch } = useQuery<ICollectionHandleQuery>(gql(collectionHandleQuery.query), {
    variables,
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (!_data.collection) {
        navigate(URLS.HOME);
        return;
      }

      prevFilter.current = variables.filters;

      setCollectionData(_data);

      setProducts(_data.collection.products.edges);

      // if (Object.keys(queryString).includes("view") && queryString.view === "seperate") {
      setIsLoading(true);

      const newExpandList = getExpandList(_data.collection.products.edges.slice(productLength.current));
      setExpandProducts([...expandProducts, ...newExpandList]);

      productLength.current = _data.collection.products.edges.length;

      setIsLoading(false);
      // }
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setMeta({
      title: `${collectionData?.collection ? `${collectionData?.collection?.title} - ` : ""} ${DEFAULT_TITLE}`,
    });
  }, [collectionData]);

  // useEffect(() => {
  //   showFooter(!hasNextPage);
  // }, [hasNextPage, pathname]);

  useEffect(() => {
    prevFilter.current = variables.filters;
  }, []);

  useEffect(() => {
    const tntFilter = sessionStorage.getItem("tnt_filter");

    if (
      JSON.stringify(prevFilter.current) !== JSON.stringify(variables.filters) ||
      tntFilter !== JSON.stringify(variables.filters)
    ) {
      void refetchData();
    }

    sessionStorage.setItem("tnt_filter", JSON.stringify(variables.filters));
  }, [variables.filters]);

  useEffect(() => {
    if (JSON.stringify(prevFilter.current) !== JSON.stringify(variables.filters)) {
      void refetchData();
    }
  }, [variables.filters]);

  const refetchData = async () => {
    const { data } = await refetch();
    if (data) {
      setCollectionData(data);
      setProducts(data.collection.products.edges);

      // if (Object.keys(queryString).includes("view") && queryString.view === "seperate") {
      setIsLoading(true);

      const newExpandList = getExpandList(data.collection.products.edges);
      setExpandProducts(newExpandList);

      productLength.current = data.collection.products.edges.length;

      setIsLoading(false);
      // }
    }
  };

  useEffect(() => {
    if (handleRef.current !== "" && handle !== handleRef.current) {
      setCollectionData(undefined);
      setProducts([]);
      void refetchData();
    }

    if (handle) handleRef.current = handle;
  }, [handle]);

  const handleFetchMore = async () => {
    await fetchMore({
      variables: { ...variables, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          collection: {
            ...prev.collection,
            products: {
              ...prev.collection.products,
              edges: [
                ...(prev.collection?.products?.edges || []),
                ...(fetchMoreResult.collection?.products?.edges || []),
              ],
              pageInfo: fetchMoreResult.collection.products.pageInfo,
            },
          },
        };
      },
    });
  };

  return {
    collectionData,
    loading: loading || isLoading,
    products:
      searchParams.get("view") && searchParams.get("view") === "combined"
        ? products
        : expandProducts.filter(({ node }) => (pathname.includes("archive") ? node.availableForSale : true)),
    productCount: products.length,
    hasNextPage,
    handleFetchMore,
  };
};

export default useCollections;
